import React, { useMemo } from "react";
import { useLocation, useSearch } from "wouter";
import { Layout } from "@/components/layout";
import { CourseCard } from "@/components/course-card";
import { useListCourses, CourseTopic, CourseFormat, CourseLevel } from "@workspace/api-client-react";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";
import { AnimatedSection } from "@/components/animated-section";

const TOPICS = ["python", "vibecoding", "ai"] as const;
const FORMATS = ["live", "async"] as const;
const LEVELS = ["middle", "high"] as const;

function readParam<T extends string>(search: URLSearchParams, key: string, allowed: readonly T[]): T | "all" {
  const v = search.get(key);
  return v && (allowed as readonly string[]).includes(v) ? (v as T) : "all";
}

export default function Courses() {
  const [, setLocation] = useLocation();
  const search = useSearch();
  const params = useMemo(() => new URLSearchParams(search), [search]);

  const topic = readParam(params, "topic", TOPICS) as CourseTopic | "all";
  const format = readParam(params, "format", FORMATS) as CourseFormat | "all";
  const level = readParam(params, "level", LEVELS) as CourseLevel | "all";

  const updateParam = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === "all") next.delete(key);
    else next.set(key, value);
    const qs = next.toString();
    setLocation("/courses" + (qs ? `?${qs}` : ""), { replace: true });
  };

  const queryParams = {
    ...(topic !== "all" && { topic }),
    ...(format !== "all" && { format }),
    ...(level !== "all" && { level }),
  };

  const { data: courses, isLoading } = useListCourses(queryParams);

  const activeFilters = [topic, format, level].filter(v => v !== "all").length;
  const resultCount = courses?.length ?? 0;

  return (
    <Layout>
      <div className="border-b border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-grid pointer-events-none opacity-50" aria-hidden="true" />
        <div className="container mx-auto px-4 py-20 md:py-24 relative z-10">
          <div className="section-tag mb-4">[ CATALOG ] · Vol. 01 / All courses</div>
          <h1 className="font-display text-5xl md:text-7xl font-bold leading-[1.02] tracking-tight mb-6 text-balance">
            Browse the <span className="font-italic-serif text-primary">stack.</span>
          </h1>
          <p className="text-xl text-muted-foreground max-w-2xl">
            Modern, hands-on tech courses for ambitious middle and high school students. Filter by topic, format, or grade.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 flex flex-col md:flex-row gap-12">
        <aside className="w-full md:w-60 shrink-0 space-y-8 md:sticky md:top-24 md:self-start">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground border-b border-border pb-3 flex items-center justify-between">
            <span>// Filters</span>
            {activeFilters > 0 && (
              <button
                onClick={() => setLocation("/courses", { replace: true })}
                className="text-primary hover:underline"
              >
                clear ({activeFilters})
              </button>
            )}
          </div>
          <div>
            <Label htmlFor="filter-topic" className="text-base font-bold mb-3 block">Topic</Label>
            <Select value={topic} onValueChange={(val) => updateParam("topic", val)}>
              <SelectTrigger id="filter-topic">
                <SelectValue placeholder="Any Topic" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Topic</SelectItem>
                <SelectItem value="python">Python</SelectItem>
                <SelectItem value="vibecoding">Vibecoding</SelectItem>
                <SelectItem value="ai">AI</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter-format" className="text-base font-bold mb-3 block">Format</Label>
            <Select value={format} onValueChange={(val) => updateParam("format", val)}>
              <SelectTrigger id="filter-format">
                <SelectValue placeholder="Any Format" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Format</SelectItem>
                <SelectItem value="live">Live Zoom</SelectItem>
                <SelectItem value="async">Self-Paced</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="filter-level" className="text-base font-bold mb-3 block">Grade Level</Label>
            <Select value={level} onValueChange={(val) => updateParam("level", val)}>
              <SelectTrigger id="filter-level">
                <SelectValue placeholder="Any Level" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Any Level</SelectItem>
                <SelectItem value="middle">Middle School</SelectItem>
                <SelectItem value="high">High School</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </aside>

        <div className="flex-1 min-w-0">
          {!isLoading && (
            <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-6 pb-3 border-b border-border">
              {resultCount > 0 ? `Showing ${resultCount} ${resultCount === 1 ? "course" : "courses"}` : "—"}
            </div>
          )}
          {isLoading ? (
            <div
              className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6"
              role="status"
              aria-busy="true"
              aria-label="Loading courses"
            >
              {[...Array(6)].map((_, i) => (
                <div key={i} className="flex flex-col h-[400px]">
                  <Skeleton className="h-48 w-full rounded-t-xl" />
                  <div className="p-4 border border-t-0 rounded-b-xl flex-1 flex flex-col">
                    <Skeleton className="h-4 w-1/4 mb-4" />
                    <Skeleton className="h-6 w-3/4 mb-2" />
                    <Skeleton className="h-4 w-full mb-2" />
                    <Skeleton className="h-4 w-2/3 mt-auto" />
                  </div>
                </div>
              ))}
              <span className="sr-only">Loading courses&hellip;</span>
            </div>
          ) : courses && courses.length > 0 ? (
            <AnimatedSection
              className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6"
              staggerChildren={0.06}
            >
              {courses.map(course => (
                <CourseCard key={course.id} course={course} />
              ))}
            </AnimatedSection>
          ) : (
            <div className="py-24 text-center border-2 border-dashed border-border rounded-xl bg-muted/30">
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" aria-hidden="true" />
              <h3 className="font-display text-2xl font-bold mb-2">No courses match those filters</h3>
              <p className="text-muted-foreground">Try clearing some filters to widen the search.</p>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
